import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { NovaIcon } from "@rds-nova/angular-icon";
import {
  NovaTable,
  NovaTableBody,
  NovaTableCell,
  NovaTableColumn,
  NovaTableHead,
  NovaTableHeadRow,
  NovaTableRow,
} from "@rds-nova/angular-table";
import { NovaTag } from "@rds-nova/angular-tag";
import { ClosingDetailEntry, ClosingDetailMap } from "../../+state/ron-monitor.store";
import { ClosingPill, NotaryLane, SlotVariant } from "../../models/ron-monitor.models";
import {
  GridLane,
  SlotAxis,
  SlotColumn,
  SlotPresentation,
  VISIBLE_SLOTS,
  bucketClosings,
  bucketKey,
  buildGridLanes,
  buildSlotAxis,
  clampWindowStart,
  currentSlotIndex,
  formatClockTime,
  initialWindowStart,
  isOpenSlot,
  laneIdForClosing,
  matchingLaneIds,
  nextSlotLabel,
  openSlotPresentation,
  slotPresentation,
} from "../../ron-closing-monitor.utils";
import { OrderDetailPanelComponent } from "../order-detail-panel/order-detail-panel.component";
import { SlotPillComponent } from "./slot-pill.component";

export interface GridCell {
  key: string;
  isCurrent: boolean;
  packageId: number | null;
  pill: ClosingPill | null;
  presentation: SlotPresentation;
  interactive: boolean;
  expanded: boolean;
  controls: string | null;
}

export interface GridPanel {
  packageId: number;
  panelId: string;
  variant: SlotVariant;
  nextSlot: string;
  ariaLabel: string;
}

export interface GridRow {
  lane: GridLane;
  isMatch: boolean;
  cells: GridCell[];
  panels: GridPanel[];
}

@Component({
  selector: "app-notary-grid",
  templateUrl: "./notary-grid.component.html",
  styleUrls: ["./notary-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NovaIcon,
    NovaTable,
    NovaTableBody,
    NovaTableCell,
    NovaTableColumn,
    NovaTableHead,
    NovaTableHeadRow,
    NovaTableRow,
    NovaTag,
    OrderDetailPanelComponent,
    SlotPillComponent,
  ],
})
export class NotaryGridComponent implements OnChanges, AfterViewChecked {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() lanes: NotaryLane[] = [];
  @Input() closings: ClosingPill[] = [];
  @Input() now: Date | null = null;
  @Input() searchTerm = "";
  @Input() expandedPackageIds: number[] = [];
  @Input() details: ClosingDetailMap = {};

  @Output() pillActivate = new EventEmitter<ClosingPill>();
  @Output() panelRetry = new EventEmitter<number>();
  @Output() panelDismiss = new EventEmitter<number>();

  readonly axis: SlotAxis = buildSlotAxis();
  readonly laneColumnWidth = "178px";
  readonly arrowColumnWidth = "24px";
  readonly currentColumnWidth = "176px";
  readonly slotColumnWidth = "148px";

  visibleColumns: SlotColumn[] = [];
  rows: GridRow[] = [];
  currentSlotColumnIndex: number | null = null;
  canPageBack = false;
  canPageForward = false;

  private windowStart = 0;
  private windowInitialized = false;
  private pendingScrollLaneId: string | null = null;
  private pendingFocusPackageId: number | null = null;
  private pendingDismissPackageIds: number[] = [];

  get panelColumnSpan(): number {
    return this.visibleColumns.length + 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.rebuild();

    if (changes["searchTerm"] && !changes["searchTerm"].firstChange) {
      this.pendingScrollLaneId =
        this.rows.find((row) => row.isMatch)?.lane.laneId ?? null;
    }
  }

  ngAfterViewChecked(): void {
    this.flushPendingScroll();
    this.flushPendingFocus();
    this.flushPendingDismiss();
  }

  entryFor(packageId: number): ClosingDetailEntry | null {
    return this.details[packageId] ?? null;
  }

  onPanelDismiss(packageId: number): void {
    this.pendingFocusPackageId = packageId;
    this.panelDismiss.emit(packageId);
  }

  pageBy(delta: number): void {
    const next = clampWindowStart(this.windowStart + delta, this.axis.length);
    if (next === this.windowStart) {
      return;
    }
    this.windowStart = next;
    this.rebuild();
  }

  onPillActivate(cell: GridCell): void {
    if (cell.pill) {
      this.pillActivate.emit(cell.pill);
    }
  }

  trackByLaneId(_index: number, row: GridRow): string {
    return row.lane.laneId;
  }

  trackByCellKey(_index: number, cell: GridCell): string {
    return cell.key;
  }

  trackBySlotIndex(_index: number, column: SlotColumn): number {
    return column.index;
  }

  trackByPanelId(_index: number, panel: GridPanel): string {
    return panel.panelId;
  }

  private flushPendingScroll(): void {
    if (this.pendingScrollLaneId === null) {
      return;
    }
    const laneId = this.pendingScrollLaneId;
    this.pendingScrollLaneId = null;
    this.host.nativeElement
      .querySelector(`[data-lane-id="${CSS.escape(laneId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }

  private flushPendingFocus(): void {
    if (this.pendingFocusPackageId === null) {
      return;
    }
    const packageId = this.pendingFocusPackageId;
    if (this.expandedPackageIds.includes(packageId)) {
      return;
    }
    this.pendingFocusPackageId = null;
    this.host.nativeElement
      .querySelector<HTMLElement>(
        `[data-package-id="${packageId}"] .rcm-pill--interactive`
      )
      ?.focus();
  }

  private rebuild(): void {
    const lanes = buildGridLanes(this.lanes, this.closings);
    const buckets = bucketClosings(this.closings, this.axis);
    const current = this.now ? currentSlotIndex(this.now, this.axis) : null;

    if (!this.windowInitialized && lanes.length > 0) {
      this.windowStart = initialWindowStart(this.now, this.axis);
      this.windowInitialized = true;
    }
    this.windowStart = clampWindowStart(this.windowStart, this.axis.length);

    const matches = matchingLaneIds(this.searchTerm, lanes, this.closings);
    const expanded = new Set(this.expandedPackageIds);
    this.currentSlotColumnIndex = current;

    this.visibleColumns = this.axis.slice(
      this.windowStart,
      this.windowStart + VISIBLE_SLOTS
    );

    const cellsByLane = lanes.map((lane) =>
      this.visibleColumns.map((column) =>
        this.buildCell(lane, column, buckets, current, expanded)
      )
    );
    const inView = new Set<number>();
    cellsByLane.forEach((cells) =>
      cells.forEach((cell) => {
        if (cell.packageId !== null) {
          inView.add(cell.packageId);
        }
      })
    );

    this.rows = lanes.map((lane, index) => ({
      lane,
      isMatch: matches.has(lane.laneId),
      cells: cellsByLane[index],
      panels: this.buildPanels(lane, expanded, inView),
    }));

    const shown = new Set(
      this.rows.flatMap((row) => row.panels.map((panel) => panel.packageId))
    );
    this.pendingDismissPackageIds = this.expandedPackageIds.filter(
      (packageId) => !shown.has(packageId)
    );

    this.canPageBack = this.windowStart > 0;
    this.canPageForward =
      this.windowStart < Math.max(0, this.axis.length - VISIBLE_SLOTS);
  }

  private buildPanels(
    lane: GridLane,
    expanded: Set<number>,
    inView: Set<number>
  ): GridPanel[] {
    return this.closings
      .filter(
        (closing) =>
          expanded.has(closing.packageId) &&
          inView.has(closing.packageId) &&
          laneIdForClosing(closing) === lane.laneId
      )
      .sort(
        (left, right) =>
          left.scheduledDateTime.localeCompare(right.scheduledDateTime) ||
          left.packageId - right.packageId
      )
      .map((closing) => ({
        packageId: closing.packageId,
        panelId: `rcm-closing-detail-${closing.packageId}`,
        variant: closing.variant,
        nextSlot: nextSlotLabel(closing, this.closings),
        ariaLabel: `Closing details for ${lane.name} at ${formatClockTime(
          new Date(closing.scheduledDateTime)
        )}`,
      }));
  }

  private flushPendingDismiss(): void {
    if (this.pendingDismissPackageIds.length === 0) {
      return;
    }
    const packageIds = this.pendingDismissPackageIds;
    this.pendingDismissPackageIds = [];
    packageIds.forEach((packageId) => this.panelDismiss.emit(packageId));
  }

  private buildCell(
    lane: GridLane,
    column: SlotColumn,
    buckets: Map<string, ClosingPill>,
    current: number | null,
    expanded: Set<number>
  ): GridCell {
    const open = isOpenSlot(buckets, lane.laneId, column.index);
    const pill = open
      ? null
      : buckets.get(bucketKey(lane.laneId, column.index)) ?? null;

    return {
      key: bucketKey(lane.laneId, column.index),
      isCurrent: column.index === current,
      packageId: pill?.packageId ?? null,
      pill,
      presentation: pill
        ? slotPresentation(pill.variant, pill.delayMinutes, pill.icon)
        : openSlotPresentation(),
      interactive: pill !== null,
      expanded: pill ? expanded.has(pill.packageId) : false,
      controls: pill ? `rcm-closing-detail-${pill.packageId}` : null,
    };
  }
}
